// Buddy (docs/SPEC.md 2.9 a část 6): návrhy akcí z poznámek, spotřeba AI a limity (úkol 3.0).
import { z } from 'zod'
import {
  aiActionTypeSchema,
  jobStageSchema,
  reminderChannelSchema,
  type NoteProcessingStatus,
  type TranscriptSource,
} from './enums'
import type { ApiErrorCode } from './api'

/** Datum YYYY-MM-DD v zóně organizace (modelu se předává „teď“, vrací den přesně). */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum ve tvaru YYYY-MM-DD')
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Čas ve tvaru HH:MM')
const uuidOrNull = z.string().uuid().nullable()

/**
 * Tvar `payload` podle typu návrhu. Stejný tvar vrací mock i Claude (nástroj `propose_actions`),
 * web podle něj kreslí kartu návrhu a ve 3.5 z něj schválení založí entitu.
 * `clientName` je jméno v 1. pádu (i když klient v databázi není, pak `clientId` null).
 */
export const proposalPayloadSchemas = {
  add_promise: z.object({
    clientId: uuidOrNull.or(z.string()).nullable(),
    clientName: z.string().nullable(),
    jobId: z.string().nullable().optional(),
    text: z.string().min(1).max(500),
    dueDate: isoDate.nullable(),
  }),
  create_task: z.object({
    title: z.string().min(1).max(200),
    dueDate: isoDate.nullable(),
    jobId: z.string().nullable().optional(),
  }),
  create_event: z.object({
    clientId: uuidOrNull.or(z.string()).nullable(),
    clientName: z.string().nullable(),
    jobId: z.string().nullable().optional(),
    title: z.string().min(1).max(200),
    date: isoDate.nullable(),
    time: hhmm.nullable(),
    location: z.string().max(200).nullable().optional(),
  }),
  create_job: z.object({
    clientId: uuidOrNull.or(z.string()).nullable(),
    clientName: z.string().nullable(),
    title: z.string().min(1).max(200),
    stage: jobStageSchema.default('lead'),
    description: z.string().max(2000).nullable().optional(),
  }),
  create_client: z.object({
    name: z.string().min(2).max(160),
    phone: z.string().max(20).nullable(),
    email: z.string().max(160).nullable().optional(),
  }),
  update_job_stage: z.object({
    jobId: z.string().min(1),
    jobTitle: z.string().nullable().optional(),
    stage: jobStageSchema,
  }),
  schedule_reminder: z.object({
    entityType: z.enum(['job', 'promise', 'task', 'event']),
    entityId: z.string().min(1),
    /** Text připomínky (co se má připomenout). */
    title: z.string().max(200).nullable().optional(),
    dueAt: z.string(),
    channel: reminderChannelSchema.default('push'),
  }),
  draft_message: z.object({
    clientId: uuidOrNull.or(z.string()).nullable(),
    clientName: z.string().nullable(),
    channel: z.enum(['sms', 'email', 'whatsapp']),
    intent: z.string().max(200),
    text: z.string().min(1).max(2000),
  }),
} as const

/** Jeden návrh tak, jak ho vrací poskytovatel AI (před uložením do ai_actions). */
export const proposalSchema = z
  .object({
    type: aiActionTypeSchema,
    payload: z.record(z.string(), z.unknown()),
    /** Doslovná citace z přepisu (CLAUDE.md pravidlo 9), musí být podřetězcem vstupu. */
    sourceQuote: z.string().min(3).max(1000),
    confidence: z.number().min(0).max(1),
  })
  .superRefine((value, ctx) => {
    const schema = proposalPayloadSchemas[value.type]
    const res = schema.safeParse(value.payload)
    if (!res.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payload'],
        message: res.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '),
      })
    }
  })
export type ProposalInput = z.infer<typeof proposalSchema>

/** Spotřeba jednoho volání modelu (tokeny), do tabulky ai_usage. */
export interface TokenUsage {
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  durationMs: number
}

export const AI_FEATURES = ['extract', 'chat', 'digest', 'lead'] as const
export type AiFeature = (typeof AI_FEATURES)[number]

/** Nejdelší poznámka pro Buddyho (znaků). */
export const AI_NOTE_MAX_LENGTH = 4000

/** POST /v1/ai/notes: textová poznámka pro Buddyho (hlasovka s audiem přijde v 3.2). */
export const createAiNoteSchema = z.object({
  text: z.string().trim().min(3, 'Napiš aspoň pár slov').max(AI_NOTE_MAX_LENGTH),
  clientId: z.string().uuid().optional(),
  jobId: z.string().uuid().optional(),
})
export type CreateAiNoteInput = z.infer<typeof createAiNoteSchema>

/**
 * POST /v1/clients/:id/notes a POST /v1/jobs/:id/notes (hlídač slibů, úkol 3.4): poznámka z detailu
 * klienta nebo zakázky. Klient a zakázka se berou z adresy, v těle je jen text.
 */
export const createEntityNoteSchema = createAiNoteSchema.pick({ text: true })
export type CreateEntityNoteInput = z.infer<typeof createEntityNoteSchema>

/** Odpověď 202: úloha ve frontě, nebo zpracováno hned (bez fronty, testy a vývoj bez pg-boss). */
export interface AiNoteResponse {
  interactionId: string
  status: 'queued' | 'processed'
  /** Počet návrhů, jen když status = processed. */
  proposals?: number
}

/** Co vzniklo schválením návrhu (ai_actions.result): entita a odkaz v aplikaci. */
export interface ProposalResult {
  entityType: 'client' | 'job' | 'promise' | 'event' | 'task' | 'reminder' | 'interaction'
  entityId: string
  /** Cesta v aplikaci (např. /sliby, /zakazky/<id>). */
  url: string
  /** Pro update_job_stage: původní fáze kvůli vrácení zpět. */
  previousStage?: string
  /** Pro update_job_stage: fáze, která se skutečně nastavila (i po úpravě vstupu); porovnává ji vrácení zpět. */
  appliedStage?: string
  /** draft_message: odkaz `sms:` nebo `mailto:` s textem, který uživatel odešle sám. */
  href?: string
  /** create_client: použil se existující klient (shoda telefonu nebo emailu). */
  existing?: boolean
}

/** GET /v1/ai/proposals?status=proposed */
export interface AiProposalItem {
  id: string
  type: string
  status: string
  payload: Record<string, unknown>
  sourceQuote: string
  sourceInteractionId: string
  confidence: number | null
  promptVersion: string | null
  model: string | null
  createdAt: string
  /** Vyplněné po rozhodnutí (approved, auto, rejected, undone). */
  result: ProposalResult | null
  decidedAt: string | null
  /** Do kdy jde schválený nebo automaticky provedený návrh vrátit zpět (24 h). */
  undoUntil: string | null
  rejectReason: string | null
}
export interface AiProposalsResponse {
  data: AiProposalItem[]
}

/** GET /v1/ai/usage: spotřeba organizace za aktuální měsíc a limit. */
export interface AiUsageResponse {
  /** YYYY-MM v zóně organizace. */
  month: string
  calls: number
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  /** Odhad nákladů v haléřích podle ceníku modelu. */
  costMinor: number
  /** Měsíční limit tokenů (vstup + výstup), null = bez limitu. */
  limitTokens: number | null
  remainingTokens: number | null
  /** false = globální vypínač AI_ENABLED nebo vyčerpaný limit. */
  enabled: boolean
  provider: string
}

// Chat s Buddym (SPEC 2.9, úkol 3.1): POST /v1/ai/chat streamuje odpověď přes SSE.

export const CHAT_MAX_MESSAGES = 20

/** Nejdelší odpověď asistenta v historii konverzace (znaků); zprávy uživatele mají strop AI_NOTE_MAX_LENGTH. */
export const CHAT_ASSISTANT_MAX_LENGTH = 12000

export const chatMessageSchema = z.discriminatedUnion('role', [
  z.object({
    role: z.literal('user'),
    content: z.string().trim().min(1).max(AI_NOTE_MAX_LENGTH),
  }),
  z.object({
    role: z.literal('assistant'),
    content: z.string().trim().min(1).max(CHAT_ASSISTANT_MAX_LENGTH),
  }),
])
export type ChatMessage = z.infer<typeof chatMessageSchema>

/**
 * Historie konverzace drží web (jen pro tuto relaci): začíná zprávou uživatele, role se střídají
 * a poslední zpráva musí být od uživatele.
 */
export const chatRequestSchema = z
  .object({
    messages: z.array(chatMessageSchema).min(1).max(CHAT_MAX_MESSAGES),
  })
  .refine((v) => v.messages[0]?.role === 'user', {
    message: 'První zpráva musí být od uživatele',
    path: ['messages'],
  })
  .refine((v) => v.messages.every((m, i) => i === 0 || m.role !== v.messages[i - 1]!.role), {
    message: 'Zprávy uživatele a asistenta se musí střídat',
    path: ['messages'],
  })
  .refine((v) => v.messages.at(-1)?.role === 'user', {
    message: 'Poslední zpráva musí být od uživatele',
    path: ['messages'],
  })
export type ChatRequest = z.infer<typeof chatRequestSchema>

/** Nástroje chatu (SPEC část 6). org_id vždy z kontextu požadavku, nikdy z modelu. */
export const CHAT_TOOLS = [
  'find_client',
  'list_due_items',
  'summarize_client',
  'propose_actions',
] as const
export type ChatToolName = (typeof CHAT_TOOLS)[number]

export const findClientInputSchema = z.object({
  query: z.string().trim().max(120).optional(),
  phone: z.string().trim().max(20).optional(),
})
export const DUE_RANGES = ['overdue', 'today', 'tomorrow', 'week'] as const
export const listDueItemsInputSchema = z.object({
  range: z.enum(DUE_RANGES).default('today'),
})
export const summarizeClientInputSchema = z.object({ clientId: z.string().uuid() })
export const proposeActionsInputSchema = z.object({ proposals: z.array(proposalSchema).max(20) })

export interface ChatClientHit {
  id: string
  name: string
  phoneE164: string | null
  lastActivityAt: string | null
  openJobs: number
}
export interface FindClientResult {
  clients: ChatClientHit[]
}

export interface DueEventItem {
  id: string
  title: string
  startAt: string
  endAt: string
  allDay: boolean
  clientName: string | null
}
export interface DuePromiseItem {
  id: string
  text: string
  dueAt: string
  clientName: string | null
  overdue: boolean
}
export interface DueTaskItem {
  id: string
  title: string
  dueAt: string | null
  overdue: boolean
}
export interface ListDueItemsResult {
  range: (typeof DUE_RANGES)[number]
  /** Rozsah dnů v zóně firmy (YYYY-MM-DD), pro overdue jen `to`. */
  from: string
  to: string
  events: DueEventItem[]
  promises: DuePromiseItem[]
  tasks: DueTaskItem[]
}

export interface SummarizeClientResult {
  client: {
    id: string
    name: string
    phoneE164: string | null
    email: string | null
    city: string | null
    note: string | null
    lastActivityAt: string | null
  }
  openJobs: Array<{ id: string; title: string; stage: string; estimatedAmountMinor: number | null }>
  openPromises: Array<{ id: string; text: string; dueAt: string; overdue: boolean }>
  nextEvents: Array<{ id: string; title: string; startAt: string }>
  /** Posledních 5 interakcí (typ, kdy, shrnutí nebo začátek textu). */
  recentInteractions: Array<{
    id: string
    type: string
    occurredAt: string
    summary: string | null
  }>
}

export interface ProposeActionsResult {
  created: AiProposalItem[]
  /** Kolik návrhů se zahodilo (bez citace v poslední zprávě, neznámý klient nebo zakázka). */
  dropped: number
}

/** Události SSE z POST /v1/ai/chat (pole `event` = type, `data` = JSON celé události). */
export type ChatEvent =
  | { type: 'start'; interactionId: string }
  | { type: 'text'; delta: string }
  | { type: 'tool'; name: ChatToolName; status: 'running' | 'done' | 'failed' }
  | { type: 'proposal'; proposal: AiProposalItem }
  | {
      type: 'done'
      text: string
      proposals: number
      usage: { inputTokens: number; outputTokens: number }
    }
  | { type: 'error'; code: ApiErrorCode; message: string }

// Hlasová poznámka (SPEC 2.9, úkol 3.2): nahrávka + případný text ze zařízení -> přepis -> extrakce.

export const VOICE_NOTE_MAX_SECONDS = 180
export const VOICE_NOTE_MAX_BYTES = 12 * 1024 * 1024
/** Co prohlížeče a telefony nahrávají (MediaRecorder): webm/opus (Chrome, Android), mp4 (iOS), ogg. */
export const VOICE_MIME_TYPES = [
  'audio/webm',
  'audio/ogg',
  'audio/mp4',
  'audio/mpeg',
  'audio/wav',
  'audio/x-m4a',
  'audio/aac',
] as const
export type VoiceMimeType = (typeof VOICE_MIME_TYPES)[number]

/**
 * Textová pole multipart požadavku POST /v1/ai/voice-notes (soubor je v poli `audio`).
 * `noteId` vzniká v zařízení (outbox) a slouží jako idempotenční klíč: opakované nahrání téže
 * hlasovky vrátí stejnou interakci.
 */
export const voiceNoteFieldsSchema = z.object({
  noteId: z.string().uuid(),
  /** Text rozpoznaný na zařízení (Web Speech API, nativní rozpoznávání); prázdný = přepsat na serveru. */
  deviceText: z.string().trim().max(AI_NOTE_MAX_LENGTH).optional().or(z.literal('')),
  durationMs: z.coerce
    .number()
    .int()
    .min(0)
    .max(VOICE_NOTE_MAX_SECONDS * 1000)
    .optional(),
  clientId: z.string().uuid().optional().or(z.literal('')),
  jobId: z.string().uuid().optional().or(z.literal('')),
})
export type VoiceNoteFields = z.infer<typeof voiceNoteFieldsSchema>

/** Odpověď 202 na nahranou hlasovku. */
export interface VoiceNoteResponse {
  interactionId: string
  /** queued = ve frontě (přepis, pak extrakce); processed = bez fronty hotovo hned. */
  status: 'queued' | 'processed'
  processingStatus: NoteProcessingStatus
  transcript: string | null
  transcriptSource: TranscriptSource | null
  /** true = stejná hlasovka už byla nahraná (noteId), vrací se původní interakce. */
  duplicate: boolean
}

/** GET /v1/ai/notes/:id: stav zpracování poznámky nebo hlasovky, přepis a návrhy z ní. */
export interface AiNoteDetail {
  id: string
  type: string
  occurredAt: string
  transcript: string | null
  transcriptSource: TranscriptSource | null
  /** Podepsaná adresa nahrávky (platí hodinu), null u textové poznámky. */
  recordingUrl: string | null
  durationMs: number | null
  processingStatus: NoteProcessingStatus | null
  processingError: string | null
  proposals: AiProposalItem[]
}

// Schvalování návrhů (SPEC 2.9 Návrh, úkol 3.5): schválení provede akci v transakci a zapíše timeline.

export const UNDO_WINDOW_HOURS = 24

/**
 * POST /v1/ai/proposals/:id/approve. Bez `input` server převede payload návrhu na vstup entity
 * (datum v zóně firmy, výchozí časy). S `input` (po „Upravit“) se použije zadaný vstup entity
 * (tvar jako u ručního založení: createPromiseSchema, createTaskSchema, createEventSchema,
 * createJobSchema, createClientSchema, changeStageSchema), server ho validuje podle typu návrhu.
 */
export const approveProposalSchema = z.object({
  input: z.record(z.string(), z.unknown()).optional(),
})
export type ApproveProposalInput = z.infer<typeof approveProposalSchema>

export const rejectProposalSchema = z.object({
  reason: z.string().trim().max(500).optional().or(z.literal('')),
})
export type RejectProposalInput = z.infer<typeof rejectProposalSchema>

/** Odpověď approve, reject i undo: aktuální stav návrhu. */
export interface ProposalDecisionResponse {
  proposal: AiProposalItem
}

/**
 * 422 s kódem `validation` a `details.needsInput = true`: návrh nejde provést bez doplnění
 * (např. slib bez klienta nebo termínu). Web otevře formulář s předvyplněním („Upravit“).
 */
export interface ProposalNeedsInputDetails {
  needsInput: true
  missing: string[]
}

/** Události SSE z GET /v1/stream (pole `event` = type, `data` = JSON celé události). */
export type StreamEvent =
  | { type: 'hello'; at: string }
  | { type: 'proposal.created'; proposal: AiProposalItem }
  | { type: 'proposal.decided'; proposal: AiProposalItem }
  | { type: 'note.processed'; interactionId: string; processingStatus: NoteProcessingStatus }
  | { type: 'lead.created'; jobId: string; clientName: string }
  /** Uživatel spároval lehký kanál (Telegram); sběrnice je per firma, web událost použije jen pro vlastního uživatele. */
  | { type: 'channel.linked'; provider: 'telegram'; userId: string }
  /** Spojení nahradilo novější (nad limit spojení uživatele); klient se sám znovu nepřipojuje. */
  | { type: 'superseded' }
